/**
 * Quick create menu for creating new entities from anywhere.
 * Shows module-specific creatable entities based on the current module.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGuardedNavigate } from '../../shared/hooks/useGuardedNavigate';
import { Plus } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../ui/tooltip';
import { useModule } from '../../shared/contexts/ModuleContext';
import { getCreatableEntitiesForModule, EntityConfig } from '../../shared/config/modules.config';
import { getFormEntry, hasRegisteredForm, EntityFormProps } from './entityFormRegistry';
import { useBoards } from '../../modules/tasks/hooks/useBoards';
import { BoardSelectorDialog } from './BoardSelectorDialog';
import { useCreateSession } from '../../modules/ai_chat/hooks/useChat';

interface ActiveForm {
  entityKey: string;
  FormComponent: React.ComponentType<EntityFormProps>;
  boardId?: string;
}

// Entity keys that have special action handlers instead of forms
const ACTION_BASED_ENTITIES = ['chat'];

// Entity keys that navigate to a page instead of opening a form
const NAVIGATION_BASED_ENTITIES: Record<string, string> = {
  workspace: '/tasks/workspaces/new',
  board: '/tasks/boards/new',
  recruiting_job_posting: '/recruiting/jobs/new',
  pipeline_deal: '/pipeline/deals/new',
};

interface QuickCreateMenuProps {
  trigger?: React.ReactNode;
}

export function QuickCreateMenu({ trigger }: QuickCreateMenuProps = {}) {
  const { t } = useTranslation('common');
  const { currentModule } = useModule();
  // TWO navigators on purpose. A NAVIGATION_BASED_ENTITIES entry is the user
  // picking a page off the + menu, so it owes a dirty form the usual prompt.
  // The hop to a chat session just created must NOT prompt — the app is
  // finishing the thing the user just asked it to do, and the session exists
  // either way, so "Stay" would only strand them away from it.
  const navigateGuarded = useGuardedNavigate();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeForm, setActiveForm] = useState<ActiveForm | null>(null);
  const [boardSelectorOpen, setBoardSelectorOpen] = useState(false);
  const [_pendingItemCreate, setPendingItemCreate] = useState(false);

  // Chat session creation
  const createSession = useCreateSession();

  // Get creatable entities for the current module
  const creatableEntities = useMemo(() => {
    const entities = getCreatableEntitiesForModule(currentModule.id);
    // Include entities that have registered forms, action-based handlers, or navigation-based handlers
    return entities.filter(({ entityKey }) =>
      hasRegisteredForm(entityKey) ||
      ACTION_BASED_ENTITIES.includes(entityKey) ||
      entityKey in NAVIGATION_BASED_ENTITIES
    );
  }, [currentModule.id]);

  // Fetch boards when we might need them (for item creation in work module)
  const needsBoards = currentModule.id === 'work' && menuOpen;
  const { data: boardsData } = useBoards({ enabled: needsBoards });
  const boards = boardsData?.data || [];

  // Handle creating a new chat session
  const handleCreateChat = useCallback(async () => {
    try {
      const result = await createSession.mutateAsync({ chat_type: 'assistant' });
      // Navigate to chat page with the new session
      navigate(`/ai-chat?session=${result.data.id}`);
    } catch {
      // The global mutation-error handler shows the specific translated toast.
    }
  }, [createSession, navigate]);

  const handleCreateEntity = (entityKey: string, _entity: EntityConfig) => {
    setMenuOpen(false);

    // Handle action-based entities (no form, just an action)
    if (entityKey === 'chat') {
      handleCreateChat();
      return;
    }

    // Handle navigation-based entities (navigate to a page instead of opening a form)
    if (entityKey in NAVIGATION_BASED_ENTITIES) {
      navigateGuarded(NAVIGATION_BASED_ENTITIES[entityKey]);
      return;
    }

    const formEntry = getFormEntry(entityKey);
    if (!formEntry) return;

    // Special handling for items - need board context
    if (formEntry.requiresContext === 'board') {
      if (boards.length === 0) {
        // No boards - create a board first
        const boardFormEntry = getFormEntry('board');
        if (boardFormEntry) {
          setActiveForm({
            entityKey: 'board',
            FormComponent: boardFormEntry.component,
          });
        }
      } else if (boards.length === 1) {
        // One board - use it directly
        setActiveForm({
          entityKey,
          FormComponent: formEntry.component,
          boardId: boards[0].id,
        });
      } else {
        // Multiple boards - show selector
        setPendingItemCreate(true);
        setBoardSelectorOpen(true);
      }
      return;
    }

    // Standard form opening
    setActiveForm({
      entityKey,
      FormComponent: formEntry.component,
    });
  };

  const handleBoardSelect = (boardId: string) => {
    setBoardSelectorOpen(false);
    setPendingItemCreate(false);

    const formEntry = getFormEntry('task');
    if (formEntry) {
      setActiveForm({
        entityKey: 'task',
        FormComponent: formEntry.component,
        boardId,
      });
    }
  };

  const handleCloseForm = () => {
    setActiveForm(null);
  };

  // Don't render the menu if there are no creatable entities for this module
  if (creatableEntities.length === 0) {
    return null;
  }

  const defaultTrigger = (
    <Button
      variant="ghost"
      size="icon"
      className="text-inherit hover:bg-white/10"
      aria-label={t('common:app.create')}
    >
      <Plus className="h-5 w-5" />
    </Button>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                {trigger || defaultTrigger}
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {t('app.create')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {creatableEntities.map(({ entityKey, entity }) => (
                  <DropdownMenuItem
                    key={entityKey}
                    onClick={() => handleCreateEntity(entityKey, entity)}
                  >
                    {entity.icon || <Plus className="h-4 w-4" />}
                    <span className="ms-2">{t(entity.displayNameKey)}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {t('app.create')}
        </TooltipContent>
      </Tooltip>

      {/* Board selector for item creation */}
      <BoardSelectorDialog
        open={boardSelectorOpen}
        onClose={() => {
          setBoardSelectorOpen(false);
          setPendingItemCreate(false);
        }}
        onSelect={handleBoardSelect}
        boards={boards}
      />

      {/* Active form dialog */}
      {activeForm && (
        <activeForm.FormComponent
          open={true}
          onClose={handleCloseForm}
          {...(activeForm.boardId && { boardId: activeForm.boardId })}
        />
      )}
    </TooltipProvider>
  );
}
